/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useState } from "react";

import { auth, db } from "../firebase";

import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

const MAX_REPORT_DETAILS_LENGTH = 500;

const REPORT_REASONS = [
  { value: "spam", label: "Spam or misleading content" },
  { value: "harassment", label: "Harassment or abusive behavior" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "privacy", label: "Privacy or personal information" },
  { value: "copyright", label: "Copyright infringement" },
  { value: "other", label: "Other" },
];

function getContentReportKey(target) {
  return target?.contentType === "comment"
    ? `comment_${target.commentId}`
    : "topic";
}

function getContentReportDocumentId(target) {
  return target.contentType === "comment"
    ? `comment_${target.topicId}_${target.commentId}`
    : `topic_${target.topicId}`;
}

export default function ContentReportModal({ target, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setReason("");
    setDetails("");
    setSaving(false);
    setError("");
  }, [target]);

  if (!target) return null;

  function closeModal() {
    if (saving) return;
    onClose();
  }

  async function submitReport() {
    const user = auth.currentUser;

    if (!user || !target || saving) return;

    if (!reason) {
      setError("Please select a reason for the report.");
      return;
    }

    const reportRef = doc(
      db,
      "users",
      user.uid,
      "moderationReports",
      getContentReportDocumentId(target),
    );

    setSaving(true);
    setError("");

    try {
      const existingReport = await getDoc(reportRef);

      if (!existingReport.exists()) {
        await setDoc(reportRef, {
          contentType: target.contentType,
          topicId: target.topicId,
          commentId: target.commentId,
          reporterUid: user.uid,
          reportedUserUid: target.reportedUserUid,
          reportedUserName: target.reportedUserName,
          contentTitle: target.contentTitle,
          contentBody: target.contentBody,
          reason,
          details: details.trim(),
          status: "pending",
          createdAt: serverTimestamp(),
        });
      }

      onSubmitted(getContentReportKey(target));
      onClose();
    } catch (reportError) {
      console.error("Failed to submit content report:", reportError);
      setError("The report could not be submitted. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[180] flex items-start justify-center overflow-y-auto bg-black/50 px-3 py-10 sm:items-center sm:px-6"
      onClick={closeModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-content-title"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FDFBF8] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-[#93CA87]/30 bg-[#374049] px-5 py-5 text-center sm:px-7">
          <h2
            id="report-content-title"
            className="text-3xl text-[#FCFFF6]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Report Content
          </h2>

          <p className="mt-1 text-xs leading-relaxed text-white/65">
            Your report is private and will be reviewed by BioIndex.
          </p>
        </div>

        <div className="space-y-5 p-5 sm:p-7">
          <p className="text-sm leading-6 text-[#374049]/75">
            Tell us why {target.targetLabel} may violate the Community
            Guidelines.
          </p>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="report-reason"
              className="mb-1 block text-xs font-semibold text-slate-600"
            >
              Reason
            </label>

            <select
              id="report-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setError("");
              }}
              disabled={saving}
              className="h-12 w-full rounded-xl border border-[#93CA87]/40 bg-white px-4 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
            >
              <option value="">Select a reason</option>

              {REPORT_REASONS.map((reportReason) => (
                <option key={reportReason.value} value={reportReason.value}>
                  {reportReason.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label
                htmlFor="report-details"
                className="text-xs font-semibold text-slate-600"
              >
                Additional details{" "}
                <span className="font-normal text-slate-400">(optional)</span>
              </label>

              <span className="text-[10px] text-slate-400">
                {details.length}/{MAX_REPORT_DETAILS_LENGTH}
              </span>
            </div>

            <textarea
              id="report-details"
              value={details}
              onChange={(event) =>
                setDetails(
                  event.target.value.slice(0, MAX_REPORT_DETAILS_LENGTH),
                )
              }
              rows={4}
              maxLength={MAX_REPORT_DETAILS_LENGTH}
              disabled={saving}
              placeholder="Add any context that may help us review the report."
              className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition placeholder:text-slate-400 focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-[#93CA87]/30 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
          <button
            type="button"
            onClick={closeModal}
            disabled={saving}
            className="rounded-xl border border-[#93CA87]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={submitReport}
            disabled={saving || !reason}
            className={`rounded-xl px-6 py-2.5 text-sm font-semibold text-white transition ${
              saving || !reason
                ? "cursor-not-allowed bg-slate-400"
                : "bg-[#374049] hover:bg-[#2f373e]"
            }`}
          >
            {saving ? "Submitting..." : "Submit Report"}
          </button>
        </div>
      </div>
    </div>
  );
}
